import { useRef, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
  submitCorporateInquiry,
  type CompanySize,
  type GivingInterest,
} from '@/lib/corporate-inquiry';
import { pushAnalyticsEvent } from '@/lib/analytics';

interface CorporateInquiryFormProps {
  source: string;
  className?: string;
  initialSelectedTier?: string | null;
  initialSelectedAmount?: number | null;
}

type FormState = 'idle' | 'submitting' | 'success';

const COMPANY_SIZE_OPTIONS: Array<{ value: CompanySize; label: string }> = [
  { value: '1-50', label: '1–50 employees' },
  { value: '51-200', label: '51–200 employees' },
  { value: '201-1000', label: '201–1,000 employees' },
  { value: '1000+', label: '1,000+ employees' },
];

const GIVING_INTEREST_OPTIONS: Array<{ value: GivingInterest; label: string }> = [
  { value: 'gold', label: 'Gold partnership' },
  { value: 'silver', label: 'Silver partnership' },
  { value: 'bronze', label: 'Bronze partnership' },
  { value: 'unsure', label: 'Not sure yet' },
];

export function CorporateInquiryForm({
  source,
  className = '',
  initialSelectedTier = null,
  initialSelectedAmount = null,
}: Readonly<CorporateInquiryFormProps>): JSX.Element {
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companySize, setCompanySize] = useState<CompanySize>('1-50');
  const [givingInterest, setGivingInterest] = useState<GivingInterest>('unsure');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState>('idle');
  const [honeypot, setHoneypot] = useState('');
  const mountTime = useRef(Date.now());

  const COOLDOWN_KEY = 'gsgo_corporate_inquiry_last_ts';
  const COOLDOWN_MS = 120_000;

  const validate = (): string | null => {
    if (companyName.trim().length < 2 || companyName.trim().length > 200) {
      return 'Please enter your company name (2–200 characters).';
    }
    if (contactName.trim().length < 2 || contactName.trim().length > 120) {
      return 'Please enter a contact name (2–120 characters).';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return 'Please enter a valid email address.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrorMessage(null);

    if (honeypot.trim()) {
      setFormState('success');
      return;
    }

    if (Date.now() - mountTime.current < 1500) {
      setErrorMessage('Please review your inputs and try again.');
      return;
    }

    try {
      const last = localStorage.getItem(COOLDOWN_KEY);
      if (last && Date.now() - Number(last) < COOLDOWN_MS) {
        setErrorMessage('You just submitted recently. Please wait a bit before trying again.');
        return;
      }
    } catch {
      // localStorage unavailable; continue.
    }

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setFormState('submitting');
    try {
      await submitCorporateInquiry({
        companyName,
        contactName,
        email,
        phone,
        companySize,
        givingInterest,
        message,
        selectedTier: initialSelectedTier,
        selectedAmount: initialSelectedAmount,
        source,
      });
      setFormState('success');
      pushAnalyticsEvent({
        event: 'corporate_inquiry_submit',
        source,
        giving_interest: givingInterest,
      });
      try {
        localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
      } catch {
        // noop
      }
    } catch (err) {
      console.error('[CorporateInquiryForm] Failed to submit corporate inquiry:', err);
      setFormState('idle');
      setErrorMessage('Something went wrong. Please try again in a moment.');
    }
  };

  if (formState === 'success') {
    return (
      <div
        className={`rounded-xl border border-blue-200 bg-blue-50 p-6 text-center ${className}`}
      >
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-blue-100">
          <CheckCircle2 className="text-blue-700" size={22} />
        </div>
        <p className="text-lg font-semibold text-blue-900">Inquiry submitted</p>
        <p className="mt-1 text-sm text-blue-800">
          Thank you for your interest in partnering with GSGO. A confirmation email is on its way
          to the address you provided. Our partnerships team will contact you with next steps.
        </p>
      </div>
    );
  }

  return (
    <form
      id="corporate-inquiry-form"
      onSubmit={handleSubmit}
      className={`space-y-4 ${className}`}
      noValidate
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          opacity: 0,
        }}
      >
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Company name"
          required
          maxLength={200}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
        <input
          type="text"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          placeholder="Contact name"
          required
          maxLength={120}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Work email"
          required
          maxLength={254}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone (optional)"
          maxLength={40}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <select
          value={companySize}
          onChange={(e) => setCompanySize(e.target.value as CompanySize)}
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        >
          {COMPANY_SIZE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={givingInterest}
          onChange={(e) => setGivingInterest(e.target.value as GivingInterest)}
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
          disabled={formState === 'submitting'}
        >
          {GIVING_INTEREST_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {(initialSelectedTier || initialSelectedAmount != null) && (
        <p className="text-sm text-gray-600 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          {initialSelectedTier ? (
            <span>
              Selected tier: <strong>{initialSelectedTier}</strong>
              {initialSelectedAmount != null ? ` · $${initialSelectedAmount.toLocaleString('en-US')}` : ''}
            </span>
          ) : initialSelectedAmount != null ? (
            <span>
              Selected amount: <strong>${initialSelectedAmount.toLocaleString('en-US')}</strong>
            </span>
          ) : null}
        </p>
      )}

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Tell us about your partnership goals (optional)"
        maxLength={5000}
        rows={4}
        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1A5276] focus:outline-none focus:ring-1 focus:ring-[#1A5276]"
        disabled={formState === 'submitting'}
      />

      {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}

      <button
        type="submit"
        disabled={formState === 'submitting'}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#1A5276] px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#1A5276] focus:ring-offset-2 disabled:opacity-50"
      >
        {formState === 'submitting' ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            <span>Submitting…</span>
          </>
        ) : (
          <span>Submit partnership inquiry</span>
        )}
      </button>
    </form>
  );
}
